import { CompanyType, IdentityParameter } from "@/features/types";
import { Identity } from "@/features/ui/hooks/use-identity";
import { getJwtPayload } from "@/utils/jwt";

function isCompany(identity: Identity | null, type: CompanyType) {
  return identity?.company?.type === type;
}

export function isBuyer(identity: Identity | null) {
  return isCompany(identity, "BUYER");
}

export function isAgency(identity: Identity | null) {
  return isCompany(identity, "AGENCY");
}

export function toIdentityParameter(
  identity: Identity | null
): IdentityParameter | null {
  if (identity && identity.company) {
    return {
      companyId: identity.company.id,
      by: identity.company.type,
    };
  }
  return null;
}

export function isIdentity(candidate: any): candidate is Identity {
  const casted = candidate as Identity;
  return casted.id !== undefined && casted.company !== undefined;
}

export function pluralizeCompanyType(type: CompanyType) {
  switch (type) {
    case "AGENCY":
      return "AGENCIES";
    default:
      return type + "s";
  }
}

export function singularizeCompanyType(str: string): CompanyType {
  if (str === "agencies") {
    return "AGENCY";
  } else if (str === "buyers") {
    return "BUYER";
  } else if (str === "boutiques") {
    return "BOUTIQUE";
  }

  throw {
    message: "Sorry",
  };
}

export const SELECTED_COMPANY_ID_KEY = "selectedCompanyId";

/**
 * 이번 세션에서 아직 회사를 고르지 않았고, 고를 수 있는 회사가 2개 이상이면 true.
 *
 * 회사 선택은 sessionStorage에 저장되므로 새 탭/새 세션으로 URL에 바로 접속하면 비어 있다.
 * 이때 첫 번째 회사를 말없이 자동 선택하는 대신 선택 화면을 먼저 띄우기 위한 판단에 쓴다.
 */
export function needsCompanySelection(): boolean {
  const payload = getJwtPayload();
  if (!payload) {
    return false;
  }

  const storedCompanyId = sessionStorage.getItem(SELECTED_COMPANY_ID_KEY);
  if (storedCompanyId) {
    const storedCompanyIdNum = parseInt(storedCompanyId);
    if (payload.scopes.some((scope) => scope.id === storedCompanyIdNum)) {
      return false;
    }
  }

  return payload.scopes.length > 1;
}

export const COMPANY_ID_PARAM = "company_id";

/**
 * URL의 ?company_id=... 를 이번 세션의 선택으로 반영한다.
 *
 * 권한(JWT scopes)에 없는 회사이거나 값이 잘못됐으면 아무 것도 하지 않는다.
 * 그러면 선택이 없는 상태로 남아 회사 선택 화면이 뜬다.
 */
export function applyCompanyIdFromUrl(search: string = window.location.search) {
  const rawCompanyId = new URLSearchParams(search).get(COMPANY_ID_PARAM);
  if (!rawCompanyId) {
    return;
  }

  const companyId = parseInt(rawCompanyId);
  if (isNaN(companyId)) {
    return;
  }

  const payload = getJwtPayload();
  if (payload?.scopes.some((scope) => scope.id === companyId)) {
    selectCompany(companyId);
  }
}

export function selectCompany(companyId: number) {
  sessionStorage.setItem(SELECTED_COMPANY_ID_KEY, companyId.toString());
}

export function clearSelectedCompany() {
  sessionStorage.removeItem(SELECTED_COMPANY_ID_KEY);
}

export function getIdentity(rawCompanyId?: string): Identity | null {
  const payload = getJwtPayload();
  const storedCompanyId = sessionStorage.getItem(SELECTED_COMPANY_ID_KEY);

  if (payload) {
    let company;
    if (rawCompanyId) {
      const companyId = parseInt(rawCompanyId);
      if (!isNaN(companyId)) {
        company = payload.scopes.find((scope) => scope.id === companyId);
      }

      // if (!company) {
      //   throw {
      //     error: {
      //       code: 400,
      //       status: "INVALID_COMPANY_ID",
      //       message: "Invalid company id",
      //     },
      //   };
      // }
    }

    if (!company && payload.scopes.length > 0) {
      if (storedCompanyId) {
        const storedCompanyIdNum = parseInt(storedCompanyId);
        const storedCompany = payload.scopes.find(
          (scope) => scope.id === storedCompanyIdNum
        );
        if (storedCompany) {
          company = storedCompany;
        }
      } else {
        company = payload.scopes[0];
      }
    }

    //todo 강제로 company를 설정
    // company = {
    //   id: 1,
    //   types: "BOUTIQUE",
    // };

    return {
      id: payload.sub,
      company: company || null,
    };
  }
  return null;
}
